import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Fingerprint, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const t = localStorage.getItem("grc_admin_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const Chip = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-linen/70 border border-linen-border px-4 py-3" data-testid={testid}>
    <div className="text-2xl font-heading font-medium text-navy leading-none">{value}</div>
    <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-ink-500 font-bold">{label}</div>
  </div>
);

export const AttributionsCard = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");

  const load = () => api.get("/admin/attributions").then((r) => setData(r.data)).catch(() => setData({ rows: [], stats: {}, statuses: [] }));
  useEffect(() => { load(); }, []);

  const setStatus = async (code, status) => {
    setSaving(code);
    try {
      await api.patch(`/admin/attributions/${code}`, { status });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.code === code ? { ...r, status } : r)) }));
      toast.success(`${code} marked ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update that record.");
    } finally {
      setSaving("");
    }
  };

  if (!data) {
    return (
      <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-7 flex items-center gap-2 text-ink-500">
        <Loader2 className="animate-spin" size={16} /> Loading attribution ledger…
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-linen-border shadow-soft p-6 sm:p-7" data-testid="attributions-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-navy text-lime shrink-0">
          <Fingerprint size={18} />
        </span>
        <div>
          <h3 className="font-heading font-medium text-xl text-navy">GRC attribution ledger</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Every consumer who started an application gets a reference code. The code rides the apply URL
            and goes to the consumer by email — so the file ties back to a partner even if LendingPad drops
            the parameter. Mark records as submitted or funded as you confirm them.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Chip label="Total codes" value={data.stats.total || 0} testid="attr-stat-total" />
        <Chip label="Last 30 days" value={data.stats.last_30_days || 0} testid="attr-stat-30" />
        <Chip label="Tagged to a partner" value={data.stats.tagged_to_partner || 0} testid="attr-stat-tagged" />
        <Chip label="Submitted + funded" value={(data.stats.submitted || 0) + (data.stats.funded || 0)} testid="attr-stat-submitted" />
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="attributions-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-ink-500 bg-linen/60">
              <th className="px-3 py-2.5 font-semibold">Code</th>
              <th className="px-3 py-2.5 font-semibold">Consumer</th>
              <th className="px-3 py-2.5 font-semibold">Officer</th>
              <th className="px-3 py-2.5 font-semibold">Partner</th>
              <th className="px-3 py-2.5 font-semibold">Started</th>
              <th className="px-3 py-2.5 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.code} className="border-t border-linen-border" data-testid={`attr-row-${r.code}`}>
                <td className="px-3 py-2.5 font-bold text-navy tracking-wider">{r.code}</td>
                <td className="px-3 py-2.5">
                  <div className="text-navy font-medium">{r.full_name}</div>
                  <div className="text-[11px] text-ink-500">{r.email}</div>
                </td>
                <td className="px-3 py-2.5 text-ink-600">{r.mlo_name || "House"}</td>
                <td className="px-3 py-2.5 text-ink-600">{r.bdm_name || <span className="text-ink-400">Direct</span>}</td>
                <td className="px-3 py-2.5 text-[11px] text-ink-500">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2.5">
                  <select
                    value={r.status}
                    disabled={saving === r.code}
                    onChange={(e) => setStatus(r.code, e.target.value)}
                    data-testid={`attr-status-${r.code}`}
                    className="rounded-lg border border-linen-border bg-white px-2 py-1 text-xs text-navy"
                  >
                    {data.statuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-ink-500" data-testid="attributions-empty">
                  No reference codes yet. They're minted the moment a consumer starts an application.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttributionsCard;
